import { compareSearchTextMatchSortMeta, type SearchTextMatchSortMeta } from '@kc-console/core';

import {
  getPrimarySubtextsSearchMatchSortMeta,
  getPrimarySubtextsSortLabel,
} from './getPrimarySubtextsSearchMatchScore';

export type ComparePrimarySubtextsSearchMatchOptions = {
  /**
   * 검색 메타(`tier`, `matchIndex`)가 동일할 때 라벨 `localeCompare` 대신 동점(0)을 반환한다.
   * `Array.prototype.sort`가 안정 정렬일 때 원본 순서가 유지된다.
   */
  preserveStableOrderWhenSearchMetaTies?: boolean;
};

/**
 * 아이템에서 primary·subTexts를 꺼내 검색 매칭 점수를 계산한다.
 */
export function sortMetaPrimarySubtextsForItem<T>(
  item: T,
  searchKeyword: string,
  getPrimaryText: (item: T) => string,
  getSubTexts: (item: T) => readonly unknown[] | undefined,
): SearchTextMatchSortMeta {
  return getPrimarySubtextsSearchMatchSortMeta(
    getPrimaryText(item),
    getSubTexts(item),
    searchKeyword,
  );
}

export function scorePrimarySubtextsForItem<T>(
  item: T,
  searchKeyword: string,
  getPrimaryText: (item: T) => string,
  getSubTexts: (item: T) => readonly unknown[] | undefined,
): number {
  return sortMetaPrimarySubtextsForItem(item, searchKeyword, getPrimaryText, getSubTexts).tier;
}

/**
 * 두 아이템을 검색 점수로 비교하고, 동점이면 정렬 라벨(localeCompare base)로 비교한다.
 */
export function comparePrimarySubtextsSearchMatch<T>(
  a: T,
  b: T,
  searchKeyword: string,
  getPrimaryText: (item: T) => string,
  getSubTexts: (item: T) => readonly unknown[] | undefined,
  options?: ComparePrimarySubtextsSearchMatchOptions,
): number {
  const ma = sortMetaPrimarySubtextsForItem(a, searchKeyword, getPrimaryText, getSubTexts);
  const mb = sortMetaPrimarySubtextsForItem(b, searchKeyword, getPrimaryText, getSubTexts);
  const byMeta = compareSearchTextMatchSortMeta(ma, mb);
  if (byMeta !== 0) return byMeta;
  if (options?.preserveStableOrderWhenSearchMetaTies) return 0;
  const labelA = getPrimarySubtextsSortLabel(getPrimaryText(a), getSubTexts(a));
  const labelB = getPrimarySubtextsSortLabel(getPrimaryText(b), getSubTexts(b));
  return labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
}

/**
 * 플랫 리스트를 primary·subTexts 검색 점수 순으로 정렬한 새 배열을 반환한다.
 */
export function sortFlatByPrimarySubtextsSearchMatch<T>(
  items: readonly T[],
  searchKeyword: string,
  getPrimaryText: (item: T) => string,
  getSubTexts: (item: T) => readonly unknown[] | undefined,
  compareOptions?: ComparePrimarySubtextsSearchMatchOptions,
): T[] {
  const kw = searchKeyword.trim();
  if (!kw) return [...items];
  return [...items].sort((a, b) =>
    comparePrimarySubtextsSearchMatch(a, b, kw, getPrimaryText, getSubTexts, compareOptions),
  );
}
